import React, { useState, useCallback } from "react";
import { Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";

// Constants
const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");

// Validation schema
const validationSchema = Yup.object({
  team: Yup.string().required("Please select a team"),
  first_name: Yup.string().required("Please enter a first name").max(255, "First name must be 255 characters or less"),
  last_name: Yup.string().required("Please enter a last name").max(255, "Last name must be 255 characters or less"),
  email: Yup.string()
    .required("Please enter an email")
    .email("Please enter a valid email address")
    .max(255, "Email must be 255 characters or less"),
});

const MarketingPersonaForm = React.forwardRef(function MarketingPersonaForm({ persona, onSuccess, onClose }, ref) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = Boolean(persona);

  // Formik form management
  const formik = useFormik({
    initialValues: {
      team: persona?.team || "media_success",
      first_name: persona?.first_name || "",
      last_name: persona?.last_name || "",
      email: persona?.email || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (isSubmitting) return;

      setIsSubmitting(true);
      setSubmitting(true);

      try {
        const url = isEditing ? `/admin/marketing_personas/${persona.id}` : "/admin/marketing_personas";

        const method = isEditing ? "PATCH" : "POST";

        // Submit to Rails API
        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            marketing_persona: {
              team: values.team,
              first_name: values.first_name,
              last_name: values.last_name,
              email: values.email,
            },
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.errors?.join(", ") || `HTTP ${response.status}: ${response.statusText}`);
        }

        // Success - reset form state
        formik.resetForm();

        // Call success callback
        if (onSuccess) {
          onSuccess();
        }

        // Close modal/form
        if (onClose) {
          onClose();
        }
      } catch (error) {
        // Form submission error
        formik.setErrors({ general: error.message });
      } finally {
        setIsSubmitting(false);
        setSubmitting(false);
      }
    },
  });

  // Enhanced blur handler
  const handleFieldBlur = useCallback(
    (field, value) => {
      // Call original formik blur handler
      formik.handleBlur({ target: { name: field, value } });
    },
    [formik],
  );

  // Handle button click directly to bypass form submission entirely
  const handleSubmitButtonClick = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Trigger validation and submission
      formik.submitForm();
    },
    [formik],
  );

  // Handle Enter key press to submit form
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmitButtonClick(e);
      }
    },
    [handleSubmitButtonClick],
  );

  // Expose methods and state to parent component
  React.useImperativeHandle(ref, () => ({
    submit: handleSubmitButtonClick,
    isSubmitting,
    reset: () => {
      formik.resetForm();
      setIsSubmitting(false);
    },
  }));

  return (
    <div className="p-3">
      {/* General Error Display */}
      {formik.errors.general && (
        <div className="alert alert-danger mb-3" role="alert">
          {[formik.errors.general].flat().map((error) => (
            <div key={`formik-${error.slice(0, 20)}`}>{error}</div>
          ))}
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="team" className="form-label">
          Team <span className="text-danger">*</span>
        </label>
        <Form.Select
          id="team"
          name="team"
          value={formik.values.team}
          onChange={formik.handleChange}
          onBlur={(e) => handleFieldBlur("team", e.target.value)}
          isInvalid={formik.touched.team && formik.errors.team}
          isValid={formik.touched.team && !formik.errors.team && formik.values.team}
          className="bg-white"
        >
          <option value="">Select a team...</option>
          <option value="media_success">Media Success</option>
        </Form.Select>
        <Form.Control.Feedback type="invalid">{formik.errors.team}</Form.Control.Feedback>
      </div>

      <div className="mb-3">
        <label htmlFor="first_name" className="form-label">
          First Name <span className="text-danger">*</span>
        </label>
        <Form.Control
          type="text"
          id="first_name"
          name="first_name"
          value={formik.values.first_name}
          onChange={formik.handleChange}
          onBlur={(e) => handleFieldBlur("first_name", e.target.value)}
          onKeyDown={handleKeyDown}
          isInvalid={formik.touched.first_name && formik.errors.first_name}
          isValid={formik.touched.first_name && !formik.errors.first_name && formik.values.first_name}
          placeholder="Enter first name..."
          className="bg-white"
          autoComplete="off"
        />
        <Form.Control.Feedback type="invalid">{formik.errors.first_name}</Form.Control.Feedback>
      </div>

      <div className="mb-3">
        <label htmlFor="last_name" className="form-label">
          Last Name <span className="text-danger">*</span>
        </label>
        <Form.Control
          type="text"
          id="last_name"
          name="last_name"
          value={formik.values.last_name}
          onChange={formik.handleChange}
          onBlur={(e) => handleFieldBlur("last_name", e.target.value)}
          onKeyDown={handleKeyDown}
          isInvalid={formik.touched.last_name && formik.errors.last_name}
          isValid={formik.touched.last_name && !formik.errors.last_name && formik.values.last_name}
          placeholder="Enter last name..."
          className="bg-white"
          autoComplete="off"
        />
        <Form.Control.Feedback type="invalid">{formik.errors.last_name}</Form.Control.Feedback>
      </div>

      <div className="mb-3">
        <label htmlFor="email" className="form-label">
          Email <span className="text-danger">*</span>
        </label>
        <Form.Control
          type="email"
          id="email"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={(e) => handleFieldBlur("email", e.target.value)}
          onKeyDown={handleKeyDown}
          isInvalid={formik.touched.email && formik.errors.email}
          isValid={formik.touched.email && !formik.errors.email && formik.values.email}
          placeholder="Enter email address..."
          className="bg-white"
          autoComplete="off"
        />
        <Form.Control.Feedback type="invalid">{formik.errors.email}</Form.Control.Feedback>
      </div>
    </div>
  );
});

export default MarketingPersonaForm;
